import { useApolloMutation, useApolloQuery } from '@/gql/apolloWrapper';
import useCurrentCompany from '@/hooks/useCurrentCompany';
import useEmployees from '@/hooks/useEmployees';
import useEmployerTrainingList from '@/modules/Training/hooks/useEmployerTrainingList/useEmployerTrainingList';
import useTrainingCatalog from '@/modules/Training/hooks/useTrainingCatalog/useTrainingCatalog';

import {
  Language as TrainingLanguage,
  useEnrollUsersMutation,
  useGetCompanyVerboseQuery,
  useGetTrainingPricesQuery,
  usePurchaseSeatsMutation,
  UserInput,
} from '@/gql/generated';
import {
  EnrolledUser,
  Language,
  State,
  UserRole,
} from '@/modules/Training/types/TrainingApi';
import CachePolicy from '@/gql/CachePolicy';
import { equal as equalArrays } from '@/utils/arrays';
import currency from '@bambeehr/currency';
import useNotifications from '@bambeehr/use-notifications';
import {
  computed,
  ComputedRef,
  reactive,
  readonly,
  Ref,
  ref,
  watch,
} from '@nuxtjs/composition-api';

import { cloneDeep } from 'lodash';

interface TrainingEnrollmentFormUser {
  id: String;
  firstName: String;
  lastName: String;
  language: Language;
  avatarUrl: String;
  enrolled: Boolean;
  role: String;
  employeeType: UserRole;
  selected: Boolean;
  state: State;
}

enum TrainingEnrollmentFormTab {
  EMPLOYEE = 'w2',
  CONTRACTOR = '1099',
}

const tabOptions = [
  {
    label: 'Employees (w2)',
    value: TrainingEnrollmentFormTab.EMPLOYEE,
  },
  {
    label: 'Contractors (1099)',
    value: TrainingEnrollmentFormTab.CONTRACTOR,
  },
];

let initializedForCompany: string | null = null;

const { addError, addInfo } = useNotifications();

const { companyId } = useCurrentCompany();
const bundleId = ref('');
const topicId = ref('');
const tabModel = ref(TrainingEnrollmentFormTab.EMPLOYEE);

// Training Service Data
const fetchingEnrolled = ref(false);
const companyInTraining: Ref<any> = ref({});
const syncWithTrainingDB = () => {
  fetchingEnrolled.value = true;
  useApolloQuery(
    useGetCompanyVerboseQuery,
    { data: { companyId: companyId.value } },
    {
      data: companyInTraining,
      pending: fetchingEnrolled,
    },
    { placeholderPick: ({ company: res }) => res },
    { fetchPolicy: CachePolicy.NETWORK_ONLY }
  );
};

const prices = ref();

const getPricing = () => {
  // @ts-ignore, ignoring param error
  const { onResult: onTrainingPriceResult } = useApolloQuery(
    useGetTrainingPricesQuery
  );

  onTrainingPriceResult(({ getTrainingPrices: res }) => {
    prices.value = res;
  });
};

const bundlePrice = computed(() =>
  prices.value?.bundle ? prices.value?.bundle / 100 : 0
);

const coursePrice = computed(() =>
  prices.value?.topic ? prices.value?.topic / 100 : 0
);

const currentPurchasedBundle = computed(() =>
  companyInTraining.value?.purchasedProducts?.bundles?.find(
    (b) => b.bundleId === bundleId.value
  )
);

const currentPurchasedTopic = computed(() =>
  companyInTraining.value?.purchasedProducts?.topics?.find(
    (t) => t.topicId === topicId.value
  )
);

const isNewPurchase = computed(
  () => !currentPurchasedBundle.value && !currentPurchasedTopic.value
);

const currentIdList = computed(
  () =>
    (
      currentPurchasedTopic.value || currentPurchasedBundle.value?.topics?.[0]
    )?.enrollments?.reduce((acc, cur) => [...acc, cur.userId], []) || []
);

const trainingUsers: ComputedRef<EnrolledUser[]> = computed(() => {
  const users: EnrolledUser[] = companyInTraining.value?.users || [];

  return users
    .filter((u) => currentIdList.value.includes(u.userId))
    .map((u) => {
      return {
        ...u,
        userId: u.userMngId,
      };
    });
});

const totalSeats = computed<number>(
  () =>
    currentPurchasedTopic.value?.totalSeats ||
    currentPurchasedBundle.value?.totalSeats ||
    0
);

const unfilledSeats = computed<number>(
  () =>
    currentPurchasedTopic.value?.unfilledSeats ||
    currentPurchasedBundle.value?.unfilledSeats ||
    0
);

// App Data
const w2Employees: Ref<any[] | null> = ref(null);
const contractWorkers: Ref<any[] | null> = ref(null);
const original: Ref<TrainingEnrollmentFormUser[] | null> = ref(null);
const originalContractors: Ref<TrainingEnrollmentFormUser[] | null> = ref(null);

// Form Data
const allUsers: Ref<TrainingEnrollmentFormUser[] | null> = ref([]);
const allContractorUsers: Ref<TrainingEnrollmentFormUser[] | null> = ref([]);
const filter: Ref<String> = ref('');
const saving = ref(false);
const loadingText = ref('');

const loading: ComputedRef<Boolean> = computed(
  () => fetchingEnrolled.value || !(trainingUsers.value && w2Employees.value)
);

const setOriginal = (
  w2Values: TrainingEnrollmentFormUser[],
  contractorValues: TrainingEnrollmentFormUser[]
): void => {
  original.value = cloneDeep(w2Values);
  originalContractors.value = cloneDeep(contractorValues);
};

const deferredSelectionList = ref([] as String[]);
const mapToTrainingFormat = (ee) => {
  return {
    id: ee.id,
    firstName: ee.profile?.first_name,
    lastName: ee.profile?.last_name,
    avatarUrl: ee.profile?.avatar_url,
    role: ee.profile?.role,
    employeeType: ee.permissions?.manager
      ? UserRole.MANAGER
      : UserRole.EMPLOYEE,
    state: ee.profile?.state_work_in || ee.profile?.state,
    selected: deferredSelectionList.value.includes(ee.id),
    enrolled: false,
    language: Language.ENGLISH,
  };
};

const isEmployeeTab = computed(
  () => tabModel.value === TrainingEnrollmentFormTab.EMPLOYEE
);

const preserveSelections = (
  newList: TrainingEnrollmentFormUser[],
  existingList: TrainingEnrollmentFormUser[] | null
): void => {
  if (!existingList?.length) {
    return;
  }

  const selectedIds = new Set(
    existingList.filter((u) => u.selected).map((u) => u.id)
  );

  newList.forEach((u) => {
    if (selectedIds.has(u.id)) {
      u.selected = true;
    }
  });
};

const toForm = (): void => {
  if (!loading.value) {
    // Map w2 employees to interface
    const employeeBase: any[] = w2Employees.value || [];
    const contractorBase: any[] = contractWorkers.value || [];

    const w2: TrainingEnrollmentFormUser[] =
      employeeBase.map(mapToTrainingFormat);
    const contractors: TrainingEnrollmentFormUser[] =
      contractorBase.map(mapToTrainingFormat);

    // Extend with any already enrolled employee information
    trainingUsers.value.forEach((user: EnrolledUser) => {
      const found =
        w2.find((w2ee) => w2ee.id === user.userId) ||
        contractors.find((cont) => cont.id === user.userId);

      if (found) {
        found.enrolled = true;

        if (user.language) {
          found.language = user.language;
        }

        if (user.state) {
          found.state = user.state;
        }
      }
    });

    // Preserve selections from current lists before replacing
    preserveSelections(w2, allUsers.value);
    preserveSelections(contractors, allContractorUsers.value);

    // Always populate both lists so tab switching doesn't lose state
    allUsers.value = w2;
    allContractorUsers.value = contractors;
    setOriginal(w2, contractors);
  }
};

watch([w2Employees, trainingUsers, contractWorkers, fetchingEnrolled], () => {
  toForm();
});

const updateFilter = (filterTerm: String): void => {
  filter.value = filterTerm;
};

const activeList = computed(() =>
  isEmployeeTab.value ? allUsers.value : allContractorUsers.value
);

const enrolled: ComputedRef<TrainingEnrollmentFormUser[] | undefined> =
  computed(() => activeList.value?.filter((ee) => ee.enrolled));

const notEnrolled: ComputedRef<TrainingEnrollmentFormUser[] | undefined> =
  computed(() => activeList.value?.filter((ee) => !ee.enrolled));

const selected: ComputedRef<TrainingEnrollmentFormUser[] | undefined> =
  computed(() => activeList.value?.filter((ee) => ee.selected));

const normalizeArrToCheckEquality = (
  arr: TrainingEnrollmentFormUser[]
): any[] => {
  const toCheck: string[] = ['selected'];

  return arr
    .map((x: TrainingEnrollmentFormUser) => {
      return toCheck.map((prop: string) => x[prop]);
    })
    .flat();
};

const dirty = computed(() => {
  const originalGroup = normalizeArrToCheckEquality(
    (isEmployeeTab.value ? original.value : originalContractors.value) || []
  );
  const allUsersGroup = normalizeArrToCheckEquality(activeList.value || []);

  const isClean: Boolean =
    !original.value || equalArrays(originalGroup, allUsersGroup);

  return !isClean;
});

const overAllocated = computed<number>(() => {
  const count = (selected.value?.length || 0) - unfilledSeats.value;

  return count > 0 ? count : 0;
});

// Replace with SS calculation
const overAllocatedCost = computed(() =>
  currency(
    overAllocated.value *
      (bundleId.value ? bundlePrice.value : coursePrice.value)
  ).format()
);

const state = reactive({
  allUsers: activeList,
  enrolled,
  notEnrolled,
  selected,
  original,
  dirty,
  filter,
  saving,
  loading,
  totalSeats,
  unfilledSeats,
  overAllocated,
  overAllocatedCost,
  loadingText,
});

const findUser = (userId: String): TrainingEnrollmentFormUser => {
  const found: TrainingEnrollmentFormUser = activeList.value?.find(
    (ee) => ee.id === userId
  ) as TrainingEnrollmentFormUser;

  return found;
};

const setSelectedState = (userId: String, isSelected: Boolean): void => {
  const found: TrainingEnrollmentFormUser = findUser(userId);

  // If the user ID isn't found, we'll add it to a deferred selection list
  // This helps to prevent race conditions when the user list is still loading
  if (found) {
    found.selected = isSelected;
  } else {
    deferredSelectionList.value.push(userId);
  }
};

const select = (userIds: String[] = []): void => {
  userIds.forEach((userId) => setSelectedState(userId, true));
};

const deselect = (userIds: String[] = []): void => {
  userIds.forEach((userId) => setSelectedState(userId, false));
};

const setLanguage = (userId, langPref: Language): void => {
  const found: TrainingEnrollmentFormUser = findUser(userId);

  if (found) {
    found.language = langPref;
  }
};

const updateLanguage = (
  userIds: String[] = [],
  langPref: Language = Language.ENGLISH
) => {
  userIds.forEach((userId) => setLanguage(userId, langPref));
};

const purchaseSeats = (
  numSeats: number,
  bId: string,
  tId: string,
  callback?: Function
) => {
  const {
    onDone,
    mutate: enrollUsers,
    onError: onSaveError,
  } = useApolloMutation(usePurchaseSeatsMutation, { pending: saving });

  onSaveError(() => {
    state.saving = false;
    // Unknown error should be surfaced to user
    addError(
      'There was an error purchasing seats, please try again or contact support.'
    );
  });

  onDone(() => {
    // Refetch company data so unfilledSeats reflects the purchase.
    // This prevents double-purchase if the subsequent enroll fails
    // and the user retries.
    syncWithTrainingDB();

    if (callback) {
      callback();
    }
  });

  loadingText.value = 'Purchasing';

  enrollUsers({
    data: {
      companyId: companyId.value,
      numSeats,
      topicId: tId,
      bundleId: bId,
    },
  });
};

const enroll = (input: {
  toEnroll: UserInput[];
  bId: string;
  tId: string;
  onSave?: Function;
  onError?: Function;
}) => {
  const { toEnroll, bId, tId, onSave, onError } = input;
  const {
    onDone,
    mutate: enrollUsers,
    onError: onSaveError,
  } = useApolloMutation(useEnrollUsersMutation, { pending: saving });

  onSaveError((error) => {
    state.saving = false;
    // Refetch so seat counts are current if purchase succeeded before this failed
    syncWithTrainingDB();

    const message =
      // @ts-ignore
      error.responseErrors?.[0]?.message ||
      // @ts-ignore
      error.responseErrors?.[0]?.raw?.extensions?.exception?.message?.message ||
      // @ts-ignore
      error.responseErrors?.[0]?.raw?.extensions?.exception?.message ||
      (error as any).message ||
      'Enrollment failed. Please try again or contact support.';

    addError(message);

    if (onError) {
      onError();
    }
  });

  onDone((data) => {
    if (data) {
      const result = data.enrollUsers;
      if (result?.enrolled === false) {
        const errorMsg =
          result.errors?.[0] || 'Enrollment could not be completed.';
        addError(errorMsg);
        state.saving = false;
        syncWithTrainingDB();
        if (onError) {
          onError();
        }

        return;
      }

      const hasServerErrors = !!result?.errors?.length;

      // Show messages from the server (e.g. "All selected users are already enrolled")
      if (hasServerErrors) {
        result.errors!.forEach((msg) => addError(msg));
      }

      // Optimistically mark enrolled users so the UI immediately
      // reflects their status before the refetch completes
      const enrolledUserIds = toEnroll.map((u) => u.userId);
      activeList.value?.forEach((u) => {
        if (enrolledUserIds.includes(u.id as string)) {
          u.enrolled = true;
          u.selected = false;
        }
      });

      updateFilter('');
      syncWithTrainingDB();
      deferredSelectionList.value = [];
      state.saving = false;

      // Refresh related data sources so seat counts and enrollment
      // status stay in sync across the training UI
      const { fetchCompanyTrainings } = useEmployerTrainingList();
      fetchCompanyTrainings(true);
      const { refresh: refreshCatalog } = useTrainingCatalog();
      refreshCatalog();

      if (onSave) {
        onSave({ hasServerErrors });
      }
    }
  });

  loadingText.value = 'Enrolling';
  enrollUsers({
    data: {
      companyId: companyId.value,
      users: toEnroll,
      topicId: tId || undefined,
      bundleId: bId || undefined,
    },
  });
};

const save = async (onSave?: Function, onError?: Function): Promise<void> => {
  if (state.saving) {
    return;
  }
  state.saving = true;

  // On successful enrollment of employees:
  // * update the new "original" state with the currently saved state
  // * make sure no employee is selected
  // * reset the filter
  // * fire any callback supplied

  // @ts-ignore
  const toEnroll: UserInput[] =
    selected.value?.map((s) => ({
      // @ts-ignore
      language: s.language as TrainingLanguage,
      role: s.employeeType,
      state: s.state,
      userId: s.id,
      userMngId: s.id,
    })) || [];

  // Billing freeze (BIO-2036): hidden. Never buy seats here (purchaseSeats is
  // no longer called); only enroll into seats the company already has.
  if (overAllocated.value) {
    state.saving = false;

    return;
  }

  enroll({
    toEnroll,
    bId: bundleId.value,
    tId: topicId.value,
    onSave,
    onError,
  });
};

const cancel = () => {
  if (state.dirty) {
    if (isEmployeeTab.value) {
      allUsers.value = cloneDeep(original.value);
    } else {
      allContractorUsers.value = cloneDeep(originalContractors.value);
    }
    updateFilter('');
  }
  deferredSelectionList.value = [];
};

const useTrainingEnrollmentForm = (bId: string = '', tId: string = '') => {
  const shouldSetState = bId || tId;

  if (initializedForCompany !== companyId.value) {
    // Get W2 employees
    const { w2: rawW2, contractors: rawContractors } = useEmployees();
    watch(
      [rawW2, rawContractors],
      () => {
        // BB-1884: All user types (employees, owners, admins) can be enrolled in training
        // ENG-556: Deduplicate users by ID to prevent the same person appearing multiple times
        // The API returns all roles (role=all&self=true), which can include duplicate entries
        // for users who have multiple profiles (e.g., owner + employee)
        // ENG-1037: Drop rows missing profile.state — enrollment requires a state, and
        // owner-only profiles (no employee record) typically lack one. Without this guard
        // such users appear in the dropdown but enrollment fails server-side. This
        // restores the pre-ENG-556 behavior that was dropped during dedup refactor.
        const seenIds = new Set<string>();
        const hasEnrollableState = (ee) =>
          !!(ee?.profile?.state_work_in || ee?.profile?.state);
        w2Employees.value = rawW2.value.filter((ee) => {
          const id = ee.id || ee._id;
          if (!id || seenIds.has(id) || !hasEnrollableState(ee)) {
            return false;
          }
          seenIds.add(id);

          return true;
        });

        contractWorkers.value = rawContractors.value.filter(hasEnrollableState);
      },
      { immediate: true }
    );

    getPricing();

    initializedForCompany = companyId.value;
  }

  if (shouldSetState) {
    // Reset form-specific state for the new topic/bundle
    allUsers.value = [];
    allContractorUsers.value = [];
    original.value = null;
    originalContractors.value = null;
    filter.value = '';
    saving.value = false;
    loadingText.value = '';
    deferredSelectionList.value = [];

    syncWithTrainingDB();
    bundleId.value = bId;
    topicId.value = tId;

    tabModel.value = TrainingEnrollmentFormTab.EMPLOYEE;

    // Immediately rebuild the form from existing data so the staff list
    // is populated even when reopening the same course (where watched
    // values don't change and the watcher never fires).
    toForm();
  }

  // Return the hook's API
  return {
    state: readonly(state),
    select,
    deselect,
    updateLanguage,
    save,
    cancel,
    updateFilter,
    isFetching: fetchingEnrolled,
    tabOptions,
    tabModel,
    bundlePrice,
    isNewPurchase,
  };
};

export default useTrainingEnrollmentForm;
